import type { ConversationMessage } from '@chronosphereio/ai';
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { useQueryEditorContext } from '../../query-editor-context';

const GENERIC_ERROR_MESSAGE = 'Failed to generate a query. Please try again.';
const TIMEOUT_ERROR_MESSAGE = 'Query generation timed out. Please try again.';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    if (error.name === 'RequestNotSupportedError') return error.message;
    if (error.name === 'TimeoutError') return TIMEOUT_ERROR_MESSAGE;
  }
  return GENERIC_ERROR_MESSAGE;
}

export interface QuerySuggestion {
  query: string;
  metadata?: { version?: string; input?: string; existingQuery?: string };
}

function isAbortError(error: unknown): boolean {
  return (
    (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

/**
 * Async function that fetches an AI-generated query suggestion.
 * The consumer wraps language-specific details (API endpoint, context data,
 * model selection) into this closure — the AI edit module only knows
 * about prompts, existing queries, and conversation history.
 *
 * Pass `abortSignal` into `fetch` (or other cancellable APIs) so closing the panel
 * aborts in-flight work. Results are still ignored after `await` when `abortSignal.aborted`
 * so mocks or legacy handlers that ignore `abortSignal` cannot leak stale state.
 */
export type AIEditSubmitHandler = (params: {
  prompt: string;
  existingQuery: string;
  history: ConversationMessage[];
  abortSignal: AbortSignal;
}) => Promise<{ query: string; metadata?: { version?: string } }>;

export interface AIEditState {
  isOpen: boolean;
  inputValue: string;
  hasInputValue: boolean;
  isLoading: boolean;
  suggestion: QuerySuggestion | null;
  previousPrompts: string[];
  history: ConversationMessage[];
  errorMessage: string | null;
  open: () => void;
  close: () => void;
  cancel: () => void;
  setInputValue: (value: string) => void;
  submit: () => Promise<void>;
  accept: () => void;
  acceptAndRun: () => void;
  reject: () => void;
}

const AIEditContext = createContext<AIEditState | null>(null);

export interface AIEditProviderProps {
  onSubmit: AIEditSubmitHandler;
  /** Called after a suggestion is accepted (via accept or accept-and-run). Typically used for analytics. */
  onAccept?: () => void;
  /** Called after a suggestion is rejected. Typically used for analytics. */
  onReject?: () => void;
  children: ReactNode;
}

/**
 * State provider for the AI Edit action. Must be a descendant of QueryEditor.Root.
 *
 * Manages open/close, prompt input, loading, suggestions, prompt history,
 * and conversation history. Submission logic is consolidated here since
 * it's identical across all language-specific consumers.
 */
export function AIEditProvider({ onSubmit, onAccept, onReject, children }: AIEditProviderProps) {
  const { value, onChange, onRun } = useQueryEditorContext();

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<QuerySuggestion | null>(null);
  const [previousPrompts, setPreviousPrompts] = useState<string[]>([]);
  const [history, setHistory] = useState<ConversationMessage[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /** In-flight submit; aborted in close()/cancel() so completions are ignored and fetch() can cancel. */
  const submitAbortRef = useRef<AbortController | null>(null);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const cancel = useCallback(() => {
    submitAbortRef.current?.abort();
    submitAbortRef.current = null;
    setIsLoading(false);
  }, []);

  const close = useCallback(() => {
    submitAbortRef.current?.abort();
    submitAbortRef.current = null;
    setIsOpen(false);
    setIsLoading(false);
    setInputValue('');
    setSuggestion(null);
    setPreviousPrompts([]);
    setHistory([]);
    setErrorMessage(null);
  }, []);

  const accept = useCallback(() => {
    if (!suggestion) return;
    setSuggestion(null);
    onAccept?.();
  }, [suggestion, onAccept]);

  const acceptAndRun = useCallback(() => {
    if (!suggestion) return;
    setSuggestion(null);
    onRun?.();
    onAccept?.();
  }, [onRun, suggestion, onAccept]);

  const reject = useCallback(() => {
    if (!suggestion) return;
    onChange(suggestion.metadata?.existingQuery ?? '');
    onReject?.();
    setSuggestion(null);
  }, [suggestion, onChange, onReject]);

  const submit = useCallback(async () => {
    const prompt = inputValue.trim();
    if (!prompt || submitAbortRef.current) return;

    const ac = new AbortController();
    submitAbortRef.current = ac;
    setIsLoading(true);
    setErrorMessage(null);
    const existingQuery = value;

    try {
      const result = await onSubmit({ prompt, existingQuery, history, abortSignal: ac.signal });
      if (ac.signal.aborted) return;

      setSuggestion({
        query: result.query,
        metadata: { ...result.metadata, input: prompt, existingQuery },
      });
      onChange(result.query);
      setHistory((h) => [
        ...h,
        { type: 'request', content: prompt, existingQuery: existingQuery || undefined },
        { type: 'response', content: result.query, status: 'success' },
      ]);
    } catch (error) {
      if (ac.signal.aborted || isAbortError(error)) return;
      console.error(error);
      const message = getErrorMessage(error);
      setErrorMessage(message);
      setHistory((h) => [
        ...h,
        { type: 'request', content: prompt, existingQuery: existingQuery || undefined },
        { type: 'response', content: message, status: 'error' },
      ]);
    } finally {
      if (submitAbortRef.current === ac) {
        submitAbortRef.current = null;
      }
      if (!ac.signal.aborted) {
        setIsLoading(false);
        setPreviousPrompts((p) => [...p, prompt]);
        setInputValue('');
      }
    }
  }, [inputValue, value, onSubmit, history, onChange]);

  const ctx: AIEditState = {
    isOpen,
    inputValue,
    hasInputValue: Boolean(inputValue.trim()),
    isLoading,
    suggestion,
    previousPrompts,
    history,
    errorMessage,
    open,
    close,
    cancel,
    setInputValue,
    submit,
    accept,
    acceptAndRun,
    reject,
  };

  return <AIEditContext.Provider value={ctx}>{children}</AIEditContext.Provider>;
}

/**
 * Access state and actions from the nearest AIEditProvider.
 * Must be used inside both a QueryEditor.Root and an AIEditProvider.
 */
export function useAIEdit(): AIEditState {
  const ctx = useContext(AIEditContext);
  if (ctx === null) {
    throw new Error('AI Edit components must be rendered inside an <AIEditProvider>.');
  }
  return ctx;
}
